import classNames from 'classnames';
import ErrorBoundary from 'components/ErrorBoundary';
import calculatePrice, { calculateComparePrice } from 'helpers/calculatePrice';
import { getCheckoutId } from 'helpers/cookies';
import getCustomProduct from 'helpers/getCustomProduct';
import { addToCartKlaviyo } from 'helpers/klaviyo';
import { addToCartEvent } from 'lib/events/fb-pixel/add-to-cart';
import PropTypes from 'prop-types';
import { StorefrontContext } from 'providers/storefront';
import { useContext, useState, useEffect } from 'react';
import PcModelAddCartErrorButton from '../PcModelAddCartErrorButton';
import styles from './styles';
import { usePcConfigurator } from 'utils/PcConfiguratorContext';
import {
  checkoutLineItemsReplace,
  useCheckoutEffect
} from 'helpers/graphql/checkout';
import { useMutation } from '@apollo/client';
import {
  sendShopifyAnalytics,
  AnalyticsEventName,
  getClientBrowserParameters
} from '@shopify/hydrogen-react';
import TiktokPixel from 'tiktok-pixel';

const PcModelAddToCart = ({
  pcModel,
  text,
  options,
  disabled,
  qty,
  productType,
  customProductLink = '',
  displayVariant,
}) => {
  const { errors } = usePcConfigurator();

  const [
    lineItemReplaceMutation,
    {
      data: lineItemReplaceData,
      loading: lineItemRemoveLoading,
      error: lineItemRemoveError,
    },
  ] = useMutation(checkoutLineItemsReplace);

  const price = calculatePrice(options, pcModel.price);
  const comparePrice = calculateComparePrice(options, pcModel.compare_at);
  const sku = pcModel.prismic_uid;

  const { setIsCartOpen, setCheckout, checkout } =
    useContext(StorefrontContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const createCheckout = async (data) => {
    const response = await fetch('/api/shopify/checkout/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    const checkoutItem = await response.json();
    return checkoutItem;
  };

  const productTitle = pcModel.model_title;
  const productImage = pcModel?.thumb_image;
  
  const updateLineItemsByPrice = (checkout) => {
    if (checkout.lineItems.edges.length > 0) {
      let arrayTemp = [...checkout.lineItems.edges];
      arrayTemp.sort((a, b) => parseFloat(b.node.variant.price) - parseFloat(a.node.variant.price));
      let tempCheckout = {...checkout};
      tempCheckout.lineItems = arrayTemp;
      let LineItemIds = []
      arrayTemp.map((item) => {
        const customAttributes = item.node.customAttributes.map(item => {
          const { __typename, ...rest } = item;
          return rest;
        });
        LineItemIds.push({ 
          customAttributes:  customAttributes,
          quantity: parseInt(item.node.quantity, 10),
          variantId: item.node.variant.id
        });
      })
      const variables = {
        checkoutId: checkout.id,
        lineItems: LineItemIds
      }
      lineItemReplaceMutation({ variables });
    }
  }

  const addLineItems = async (checkoutId, data) => {
    const response = await fetch('/api/shopify/checkout/add-line-items', {
      method: 'POST',
      body: JSON.stringify({ checkoutId, data }),
    });

    const addedLineItems = await response.json();

    return addedLineItems;
  };

  const  sendAddToCart = (analyticsPageData, product, variant) => {
    const payload = {
      products: [
        {
          productGid: product.id,
          name: product.title,
          price: Number(variant.priceV2.amount),
          variantGid: window.btoa(variant.id),
          variantName: variant.title,
          category: product.handle,
          sku: product.variants.edges[0].node.sku,
          quantity: Number(qty),
        }
      ],
      ...analyticsPageData,
      ...getClientBrowserParameters(),
    };

    sendShopifyAnalytics({
      eventName: AnalyticsEventName.ADD_TO_CART,
      payload
    });
  }

  const analyticsShopData = {
    shopId: 'gid://shopify/Shop/63704072436',
    currency: 'SGD',
    acceptedLanguage: 'en',
    hasUserConsent: true,
  };

  const analytics = {
    ...analyticsShopData,
  };

  const handleAddToCart = async () => {
    setIsLoading(true);

    const { product, variant } = await getCustomProduct(
      options,
      price,
      sku,
      productTitle,
      productImage,
      productType,
      comparePrice,
      customProductLink
    );

    const values = {
      variantId: window.btoa(variant.id),
      quantity: 1,
      customAttributes: variant.customAttributes,
    };
    const checkoutId = getCheckoutId();

    if (!values.variantId) return;

    if (checkoutId) {
      const addedLineItems = await addLineItems(checkoutId, values);
      setCheckout(addedLineItems.checkoutLineItemsAdd.checkout);
      updateLineItemsByPrice(addedLineItems.checkoutLineItemsAdd.checkout);
      setIsCartOpen(true);
    } else {
      const checkoutItem = await createCheckout(values);
      setCheckout(checkoutItem.checkoutCreate.checkout);
      setIsCartOpen(true);
    }

    addToCartKlaviyo(
      productTitle,
      product.id,
      variant.sku,
      productImage,
      product.handle,
      price,
      product.tags,
      checkout
    );
    if (typeof TriplePixel !== 'undefined')
      TriplePixel('AddToCart', {
        item: variant.id,
        q: qty,
      });

    sendAddToCart(analytics, product, variant);

    TiktokPixel.init(process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID, {
      debug: false,
    });
    TiktokPixel.track('AddToCart', {
      content_type: 'product',
      quantity: qty,
      content_name: variant.sku,
      content_id: '1',
      currency: 'SGD',
      value: variant.priceV2.amount,
    });

    window.dispatchEvent(
      new CustomEvent('AddToCart', {
        detail: {
          productTitle,
          productCategory: 'Gaming',
          productId: [variant.id],
          productType: 'product',
          productPrice: variant.priceV2.amount,
          productCurrency: 'SGD',
        },
      })
    );
    addToCartEvent({
      title: productTitle,
      variantId: pcModel?.slug,
      qty: Number(qty),
      price: variant.priceV2.amount,
    });
    setIsLoading(false);
  };
  const handleOpen = () => {
    setIsOpen(true);
    setIsHelpModalOpen(false);
  };

  useEffect(() => {
    if (errors.length > 0) {
      setIsOpen(true)
    }
  }, [errors]);

  return (
    <ErrorBoundary>
      <div
        className={classNames('add-to-cart', {
          steps: displayVariant === 'step',
        })}
      >
        <PcModelAddCartErrorButton
          errors={errors}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          openModal={() => setIsOpen(true)}
        />
        {errors.length <= 0 &&
        <button
          type="button"
          className={classNames(
            `button--submit ${disabled ? 'disabled' : ''}`,
            {
              disabled: isLoading,
              'steps-button': displayVariant === 'step',
            },
          )}
          disabled={isLoading}
          onClick={() => {
            if (disabled) {
              handleOpen();
            } else {
              handleAddToCart();
            }
          }}
        >
          {isLoading ? 'Adding to Cart. Please wait...' : text}
        </button>
        }
      </div>
      <style jsx>{styles}</style>
    </ErrorBoundary>
  );
};

PcModelAddToCart.propTypes = {
  disabled: PropTypes.bool.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      compared_at_price_diff: PropTypes.number,
      group_step: PropTypes.string,
      group_title: PropTypes.string,
      id: PropTypes.number,
      image: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
      in_stock: PropTypes.bool,
      is_base: PropTypes.bool,
      is_recommended: PropTypes.bool,
      pc_model_title: PropTypes.string,
      pc_model_uid: PropTypes.string,
      price_diff: PropTypes.number,
      sku: PropTypes.string,
      sub_group_name: PropTypes.string,
      tags: PropTypes.arrayOf(
        PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            value: PropTypes.string,
          }),
        ),
      ),
      title: PropTypes.string,
      variant_id: PropTypes.number,
    }),
  ).isRequired,
  text: PropTypes.string,
  productType: PropTypes.string,
  qty: PropTypes.number,
  customProductLink: PropTypes.string,
  displayVariant: PropTypes.string,
};

PcModelAddToCart.defaultProps = {
  text: 'Add To Cart',
  productType: null,
  qty: 1,
  displayVariant: null,
};

export default PcModelAddToCart;
